import { useState, useEffect, useRef } from "react";

import SearchLedger from "./SearchLedger";
import JournalView from "./JournalView";
import SideMenu from "../General/SideMenu";
import { useDispatch, useSelector } from "react-redux";
import {
  Ask_Question,
  Pre_Loading,
  Show_Message,
} from "../General/Messagedialogbox";
import Messagedialogbox from "../General/Messagedialogbox";
import { API_FETCH, API_POST, DecryptData } from "../General/Utility";
import ReactTable from "../General/ReactTable";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

// LOCAL date (yyyy-MM-dd). toISOString() gives the UTC date, which is the
// previous day in India between 12:00 AM and 5:30 AM.
const GetToday = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// Anything that is not a valid number becomes 0 (no NaN in the table / totals)
const ToAmount = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Work in paise (integers) so 0.1 + 0.2 never breaks the tally
const ToPaise = (v) => Math.round(ToAmount(v) * 100);

// true when value has more than 2 decimal places (e.g. 10.555)
const HasMoreThan2Decimals = (v) =>
  Math.abs(ToAmount(v) * 100 - Math.round(ToAmount(v) * 100)) > 1e-6;

// Indian digit grouping: 1,23,456.00
const FormatAmt = (v) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(ToAmount(v));

// <input type="date"> accepts only yyyy-MM-dd
const NormalizeDate = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/); // dd-MM-yyyy
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return "";
};

// Block e, E, +, - in number inputs
const BlockInvalidKeys = (e) => {
  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
};

// INTERIM scroll fix (see notes). Remove once the real CSS is corrected.
const PageScrollStyle = {
  maxHeight: "calc(100vh - 90px)",
  overflowY: "auto",
  overflowX: "hidden",
};

function JournalEntry() {
  const dispatch = useDispatch();
  const _AuthParems = useSelector((state) => state.Common.Admin);

  const iScrId = 79;
  // Redux Admin is empty after a page refresh, so the DB / Branch also come
  // from the decrypted local session (set in Load_JournalEntry)
  const [strDBName, setstrDBName] = useState(_AuthParems?.DB || "");
  const [iBranchId, setiBranchId] = useState(Number(_AuthParems?.Branch) || 0);
  const [iJEId, setiJEId] = useState(0);
  const [bStatus, setbStatus] = useState(true);
  const [iUserId, setiUserId] = useState(0);
  const [strEditMode, setstrEditMode] = useState("New");
  const [iEditSNo, setiEditSNo] = useState(null); // row being edited (by SNo)
  const [bModified, setbModified] = useState(false); // unsaved changes?
  const [strVoucherNo, setstrVoucherNo] = useState("");
  const [strTransNo, setstrTransNo] = useState("");
  const [strPrefix, setstrPrefix] = useState("");
  const [iNumSeqNo, setiNumSeqNo] = useState(0);
  const [strCompCode, setstrCompCode] = useState("");
  const [strFinanFromTo, setstrFinanFromTo] = useState("");
  const [strDate, setstrDate] = useState(GetToday());
  const [iLedId, setiLedId] = useState(0);
  const [strLedName, setstrLedName] = useState("");
  const [iDebit, setiDebit] = useState(0);
  const [iCredit, setiCredit] = useState(0);
  const [strRemarks, setstrRemarks] = useState("");
  const [iSumDiffAmt, setiSumDiffAmt] = useState("0.00");
  const [iSumDebit, setiSumDebit] = useState("0.00");
  const [iSumCredit, setiSumCredit] = useState("0.00");
  const [strSumRemarks, setstrSumRemarks] = useState("");
  const [ListJournals, setListJournals] = useState([]);
  const [ListLedger, setListLedger] = useState({});
  const [IsJournalOpen, setIsJournalOpen] = useState(false);
  const [IsLedgerOpen, setIsLedgerOpen] = useState(false);
  const refDebit = useRef(null);

  useEffect(() => {
    Load_JournalEntry();
  }, []);

  // Browser refresh / tab close warning when there are unsaved changes
  useEffect(() => {
    if (!bModified) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [bModified]);

  /* ---------------------------------------------------------------- */
  /* API                                                               */
  /* ---------------------------------------------------------------- */
  const Load_JournalEntry = async () => {
    const _LocalParems = await DecryptData(localStorage.jbctrlparams);
    setstrDBName(_LocalParems.DB);
    setiBranchId(Number(_LocalParems.Branch));

    const res = await API_FETCH(
      `JournalEntry/Load_JournalEntry/${Number(_LocalParems.Branch)}/${
        _LocalParems.DB
      }`,
      dispatch
    );
    if (res) {
      const objNo =
        res.objNoFormate && res.objNoFormate.length > 0
          ? res.objNoFormate[0]
          : null;
      if (!objNo) {
        Show_Message(dispatch, "Voucher number format not configured", "info");
        return;
      }
      setstrVoucherNo(objNo.strUserNo);
      setstrTransNo(objNo.strTransNo);
      setstrPrefix(objNo.strPrefix);
      setiNumSeqNo(objNo.iNumSeqNo);
      setstrCompCode(objNo.strCompCode);
      setstrFinanFromTo(objNo.strFinanFrom);
    }
  };

  const Validate_Save = () => {
    if (
      Number(iLedId) !== 0 ||
      ToAmount(iDebit) > 0 ||
      ToAmount(iCredit) > 0
    ) {
      return "You have a pending line. Click Add / Update or clear it before saving";
    }
    if (!strVoucherNo || strVoucherNo.trim() === "") {
      return "Please Enter Voucher No";
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(strDate)) {
      return "Please Select a valid Date";
    }
    if (ListJournals.length < 2) {
      return "Atleast Two Ledger Required";
    }
    if (ToPaise(iSumDebit) !== ToPaise(iSumCredit)) {
      return "Credit and Debit Not Tally";
    }
    if (ToPaise(iSumDebit) === 0) {
      return "Journal Entry amount cannot be zero";
    }
    return "";
  };

  const Save_JournalEntry = async () => {
    const Date = strDate.split("-");
    const VoucherDate = Date[1] + "-" + Date[2] + "-" + Date[0]; // MM-dd-yyyy
    const iLoginUser = Number(_AuthParems?.UserId || 0); // confirm field name
    const objEntJE = {
      iScrId: iScrId,
      iJEId: Number(iJEId),
      strPrefix: strPrefix,
      strCompCode: strCompCode,
      strFinanFrom: strFinanFromTo,
      iNumSeqNo: Number(iNumSeqNo),
      strTransNo: strTransNo,
      strVouchNo: strVoucherNo.trim(),
      strVouchDate: VoucherDate,
      // new entry -> logged-in user, existing entry -> keep original creator
      iUserId: Number(iJEId) === 0 ? iLoginUser : Number(iUserId),
      status: bStatus,
      dDiffAmt: Number(iSumDiffAmt),
      strDBName: strDBName,
      iBranchId: Number(iBranchId),
    };
    Pre_Loading(dispatch, true);
    try {
      const res = await API_POST(
        "JournalEntry/InsUpd_JournelEntry",
        {
          objEntJE: objEntJE,
          objEntJEDT: ListJournals,
        },
        dispatch
      );
      if (res) {
        Clear_JournalEntry();
        Show_Message(dispatch, res.strMessage, "success");
      }
    } catch (e) {
      Show_Message(dispatch, e.message, "error");
    } finally {
      Pre_Loading(dispatch, false);
    }
  };

  const Delete_JournalEntry = async () => {
    Pre_Loading(dispatch, true);
    try {
      // API_POST signature is (url, body, dispatch) - dispatch was being passed as the body
      const res = await API_POST(
        "JournalEntry/Delete_JournelEntry/" +
          Number(iJEId) +
          "/" +
          strDBName,
        {},
        dispatch
      );
      if (res) {
        Clear_JournalEntry();
        Show_Message(dispatch, res.strMessage, "success");
      }
    } catch (e) {
      Show_Message(dispatch, e.message, "error");
    } finally {
      Pre_Loading(dispatch, false);
    }
  };

  const View__JournalEntry = async (iSelectedJEId) => {
    setIsJournalOpen(false);
    Pre_Loading(dispatch, true);
    try {
      const res = await API_FETCH(
        "JournalEntry/View_JournelEntry/" +
          Number(iSelectedJEId) +
          "/" +
          strDBName,
        dispatch
      );
      if (res) {
        const EntJournel = res.EntJournel;
        if (
          !EntJournel ||
          !EntJournel.objEntHT ||
          EntJournel.objEntHT.length === 0
        ) {
          Show_Message(dispatch, "Journal Entry not found", "info");
          return;
        }
        const EntHT = EntJournel.objEntHT[0];
        const ListDT = (EntJournel.objEntDT || []).map((Data, index) => ({
          ...Data,
          sNo: index + 1,
          dr: ToAmount(Data.dr),
          cr: ToAmount(Data.cr),
        }));
        setstrDate(NormalizeDate(EntHT.strVouchDate));
        setiJEId(EntHT.iJEId);
        setstrPrefix(EntHT.strPrefix);
        setstrCompCode(EntHT.strCompCode);
        setstrFinanFromTo(EntHT.strFinanFrom);
        setiNumSeqNo(EntHT.iNumSeqNo);
        setstrTransNo(EntHT.strTransNo);
        setstrVoucherNo(EntHT.strVouchNo);
        setbStatus(EntHT.status);
        setiUserId(EntHT.iUserId);
        setListJournals(ListDT);
        Calculate_Totals(ListDT);
        Reset_LineEntry();
        setbModified(false);
      }
    } catch (e) {
      console.error("View__JournalEntry", e);
      Show_Message(dispatch, e.message, "error");
    } finally {
      // success path shows no message, so nothing else would ever hide the loader
      Pre_Loading(dispatch, false);
    }
  };

  /* ---------------------------------------------------------------- */
  /* Screen actions                                                    */
  /* ---------------------------------------------------------------- */
  const Reset_LineEntry = () => {
    setiLedId(0);
    setstrLedName("");
    setiDebit(0);
    setiCredit(0);
    setstrRemarks("");
    setstrEditMode("New");
    setiEditSNo(null);
    setListLedger({});
  };

  const Calculate_Totals = (listJournal) => {
    let DebitPaise = 0;
    let CreditPaise = 0;
    listJournal.forEach((Data) => {
      DebitPaise += ToPaise(Data.dr);
      CreditPaise += ToPaise(Data.cr);
    });
    setiSumDebit((DebitPaise / 100).toFixed(2));
    setiSumCredit((CreditPaise / 100).toFixed(2));
    setiSumDiffAmt(((DebitPaise - CreditPaise) / 100).toFixed(2));
  };

  const Clear_JournalEntry = () => {
    setiJEId(0);
    setbStatus(true);
    setiUserId(0);
    setstrVoucherNo("");
    setstrDate(GetToday());
    setListJournals([]);
    setstrSumRemarks("");
    Calculate_Totals([]);
    Reset_LineEntry();
    setbModified(false);
    Load_JournalEntry();
  };

  const Confirm_Clear = () => {
    if (bModified) {
      Ask_Question(
        dispatch,
        Clear_JournalEntry,
        "Unsaved changes will be lost. Do You Want to Clear?"
      );
    } else {
      Clear_JournalEntry();
    }
  };

  const Confirm_Close = () => {
    if (bModified) {
      Ask_Question(
        dispatch,
        () => {
          window.location.href = `/JustBill/DashBoard`;
        },
        "Unsaved changes will be lost. Do You Want to Close?"
      );
    } else {
      window.location.href = `/JustBill/DashBoard`;
    }
  };

  const SelectLedger = (objled) => {
    setiLedId(objled.ledId);
    setstrLedName(objled.ledName);
    setListLedger(objled);
    setIsLedgerOpen(false);
    // keyboard flow: ledger -> debit
    setTimeout(() => {
      if (refDebit.current) refDebit.current.focus();
    }, 0);
  };

  // The ledger popup is mounted ONLY when opened. It loads the ledger list on
  // mount, so mounting it with the screen fires the API before login params are ready.
  const OpenLedger = () => setIsLedgerOpen(true);

  const DebitChange = (value) => {
    setiDebit(value);
    if (ToAmount(value) > 0) setiCredit(0); // a line is either Dr or Cr
  };

  const CreditChange = (value) => {
    setiCredit(value);
    if (ToAmount(value) > 0) setiDebit(0);
  };

  const AddTable = () => {
    const dr = ToAmount(iDebit);
    const cr = ToAmount(iCredit);

    if (Number(iLedId) === 0 || !ListLedger || !ListLedger.ledId) {
      Show_Message(dispatch, "Please Select Ledger", "info");
      return;
    }
    if (dr < 0 || cr < 0) {
      Show_Message(dispatch, "Amount cannot be negative", "info");
      return;
    }
    if (dr > 0 && cr > 0) {
      Show_Message(dispatch, "Enter either Debit or Credit, not both", "info");
      return;
    }
    if (dr === 0 && cr === 0) {
      Show_Message(dispatch, "Please Enter Debit or Credit Amount", "info");
      return;
    }
    if (HasMoreThan2Decimals(dr) || HasMoreThan2Decimals(cr)) {
      Show_Message(dispatch, "Amount allows only 2 decimal places", "info");
      return;
    }

    let NewList;
    if (strEditMode === "Modify" && iEditSNo !== null) {
      // edit by SNo (NOT by ledId - the same ledger can appear on many lines)
      NewList = ListJournals.map((Data) =>
        Data.sNo === iEditSNo
          ? {
              ...Data,
              ledName: ListLedger.ledName,
              ledCode: ListLedger.ledCode,
              ledId: ListLedger.ledId,
              dr: dr,
              cr: cr,
              remarks: strRemarks,
            }
          : Data
      );
    } else {
      NewList = [
        ...ListJournals,
        {
          sNo: ListJournals.length + 1,
          ledName: ListLedger.ledName,
          ledCode: ListLedger.ledCode,
          ledId: ListLedger.ledId,
          dr: dr,
          cr: cr,
          remarks: strRemarks,
          vouId: 0,
        },
      ];
    }
    setListJournals(NewList);
    Calculate_Totals(NewList);
    Reset_LineEntry();
    setbModified(true);
  };

  const RemoveTableRow = (RowInfo) => {
    try {
      const NewList = ListJournals.filter(
        (Data) => Data.sNo !== RowInfo.sNo
      ).map((Data, index) => ({ ...Data, sNo: index + 1 }));
      setListJournals(NewList);
      Calculate_Totals(NewList);
      if (iEditSNo !== null) Reset_LineEntry(); // row numbers have shifted
      setbModified(true);
    } catch (e) {
      Show_Message(dispatch, e.message, "error");
    }
  };

  const Modify_JournalEntry = (RowInfo) => {
    try {
      setstrLedName(RowInfo.ledName);
      setiLedId(Number(RowInfo.ledId));
      setListLedger({
        ledId: RowInfo.ledId,
        ledName: RowInfo.ledName,
        ledCode: RowInfo.ledCode,
      });
      setiDebit(RowInfo.dr);
      setiCredit(RowInfo.cr);
      setstrRemarks(RowInfo.remarks || "");
      setiEditSNo(RowInfo.sNo);
      setstrEditMode("Modify");
    } catch (e) {
      Show_Message(dispatch, e.message, "error");
    }
  };

  const LineKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      AddTable();
    } else if (e.key === "Escape" && strEditMode === "Modify") {
      Reset_LineEntry();
    }
  };

  const Save_Click = () => {
    const strError = Validate_Save();
    if (strError !== "") {
      Show_Message(dispatch, strError, "info");
    } else {
      Ask_Question(
        dispatch,
        Save_JournalEntry,
        "Do You Want to  Save JournalEntry?"
      );
    }
  };

  const Delete_Click = () => {
    if (Number(iJEId) === 0) {
      Show_Message(dispatch, "Please Select Journal Entry", "info");
    } else {
      Ask_Question(
        dispatch,
        Delete_JournalEntry,
        "Do You Want to  Delete JournalEntry?"
      );
    }
  };

  const IsTallied = ToPaise(iSumDiffAmt) === 0;

  const Columns = [
    {
      Header: "SNO",
      accessor: "sNo",
      maxWidth: 70,
    },
    {
      Header: "Ledger Code",
      accessor: "ledCode",
      show: false,
    },
    {
      Header: "Ledger Name",
      accessor: "ledName",
    },
    {
      Header: "Debit",
      accessor: "dr",
      Datatype: "Decimal",
    },
    {
      Header: "Credit",
      accessor: "cr",
      Datatype: "Decimal",
    },
    {
      Header: "Remarks",
      accessor: "remarks",
    },
    {
      Header: "Action",
      id: "ledId",
      maxWidth: 100,
      cell: ({ rowinfo }) => (
        <div
          style={{ display: "flex", justifyContent: "center", gap: "16px" }}
        >
          <i
            onClick={() => Modify_JournalEntry(rowinfo)}
            className="far fa-edit"
            title="Edit"
            style={{ cursor: "pointer" }}
          ></i>
          <i
            onClick={() => RemoveTableRow(rowinfo)}
            className="far fa-trash-alt"
            title="Delete"
            style={{ cursor: "pointer" }}
          ></i>
        </div>
      ),
    },
  ];

  return (
    <>
      <SideMenu ScrId={iScrId} />
      <div className="Master-container">
        <div className="Master-card1">
          <div style={PageScrollStyle}>
            <div className="row p-2">
              <div className="col-md-9 col-sm-12 ">
                <div className="fg-card  p-2">
                  <div className="row">
                    <div className="col-md-4 col-sm-6 mb-2">
                      <label>Voucher No *</label>
                      <input
                        type="text"
                        aria-label="Voucher No"
                        className="form-control"
                        placeholder="Voucher No"
                        value={strVoucherNo}
                        onChange={(e) => {
                          setstrVoucherNo(e.target.value);
                          setbModified(true);
                        }}
                      />
                    </div>
                    <div className="col-md-4 col-sm-6 mb-2">
                      <label>Date *</label>
                      <input
                        type="date"
                        aria-label="Voucher Date"
                        className="form-control"
                        value={strDate}
                        onChange={(e) => {
                          setstrDate(e.target.value);
                          setbModified(true);
                        }}
                      />
                    </div>
                    <div className="col-md-4 col-sm-12 mb-2">
                      <label>Ledger *</label>
                      <div className="input-group">
                        <input
                          type="text"
                          aria-label="Ledger"
                          className="form-control"
                          placeholder="Select Ledger (Click / Enter / F2)"
                          value={strLedName}
                          readOnly
                          style={{ cursor: "pointer" }}
                          onClick={OpenLedger}
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" ||
                              e.key === " " ||
                              e.key === "F2"
                            ) {
                              e.preventDefault();
                              OpenLedger();
                            }
                          }}
                        />
                        <span
                          className="input-group-text"
                          style={{ cursor: "pointer" }}
                          onClick={OpenLedger}
                          title="Search Ledger"
                        >
                          <i className="fas fa-search"></i>
                        </span>
                      </div>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-2">
                      <label>Debit</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        aria-label="Debit Amount"
                        ref={refDebit}
                        className="form-control"
                        placeholder="Enter Debit Amount"
                        onClick={(e) => e.target.select()}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          BlockInvalidKeys(e);
                          LineKeyDown(e);
                        }}
                        value={iDebit}
                        onChange={(e) => DebitChange(e.target.value)}
                        onBlur={(e) => {
                          if (e.target.value === "") setiDebit(0);
                        }}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 mb-2">
                      <label>Credit</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        aria-label="Credit Amount"
                        onClick={(e) => e.target.select()}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          BlockInvalidKeys(e);
                          LineKeyDown(e);
                        }}
                        className="form-control"
                        placeholder="Enter Credit Amount"
                        value={iCredit}
                        onChange={(e) => CreditChange(e.target.value)}
                        onBlur={(e) => {
                          if (e.target.value === "") setiCredit(0);
                        }}
                      />
                    </div>
                    <div className="col-md-3 col-sm-12 mb-2">
                      <label>Remarks</label>
                      <input
                        type="text"
                        aria-label="Line Remarks"
                        onClick={(e) => e.target.select()}
                        onKeyDown={LineKeyDown}
                        className="form-control"
                        placeholder="Remarks"
                        maxLength={250}
                        value={strRemarks}
                        onChange={(e) => setstrRemarks(e.target.value)}
                      />
                    </div>
                    <div className="col-sm-6 col-md-3 mb-2">
                      <div className="trans-inputbtn">
                        <button type="button" onClick={AddTable}>
                          {strEditMode === "Modify" ? "Update" : "Add"}
                        </button>
                        {strEditMode === "Modify" && (
                          <button
                            type="button"
                            onClick={Reset_LineEntry}
                            style={{ marginLeft: "6px" }}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <ReactTable
                    columns={Columns}
                    data={ListJournals}
                    minRows={7}
                    row_click={(rowInfo) => {}}
                    row_doubleclick={(rowInfo) => {
                      Modify_JournalEntry(rowInfo);
                    }}
                    background={true}
                    className="full-table"
                  />
                  <div className="btn-section">
                    <button
                      type="button"
                      className="btn-fabgreen"
                      onClick={Save_Click}
                    >
                      Save &nbsp; <i className="fas fa-floppy-disk"></i>
                    </button>
                    <button
                      type="button"
                      className="btn-fabgreen"
                      onClick={() => setIsJournalOpen(true)}
                    >
                      View &nbsp; <i className="fas fa-eye"></i>
                    </button>
                    <button
                      type="button"
                      className="btn-fabgreen"
                      onClick={Delete_Click}
                    >
                      Delete &nbsp; <i className="fas fa-trash"></i>
                    </button>
                    <button
                      type="button"
                      className="btn-fabgreen"
                      onClick={Confirm_Clear}
                    >
                      Clear &nbsp; <i className="fas fa-eraser"></i>
                    </button>
                    <button
                      type="button"
                      className="btn-fabgreen"
                      onClick={Confirm_Close}
                    >
                      Close &nbsp; <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-sm-12 ">
                <div className="fg-card ">
                  <div className="col-md-12 col-sm-12 mb-2">
                    <label>Debit</label>
                    <input
                      type="text"
                      aria-label="Total Debit"
                      className="form-control"
                      style={{ textAlign: "right" }}
                      value={FormatAmt(iSumDebit)}
                      readOnly
                    />
                  </div>
                  <div className="col-md-12 col-sm-12 mb-2">
                    <label>Credit</label>
                    <input
                      type="text"
                      aria-label="Total Credit"
                      className="form-control"
                      style={{ textAlign: "right" }}
                      value={FormatAmt(iSumCredit)}
                      readOnly
                    />
                  </div>
                  <div className="col-md-12 col-sm-12 mb-2">
                    <div className="net-amount">
                      <h1>DIFFERENCE</h1>
                      <h2
                        style={{ color: IsTallied ? "#198754" : "#dc3545" }}
                      >
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: "INR",
                        }).format(ToAmount(iSumDiffAmt))}
                      </h2>
                      <small
                        style={{
                          display: "block",
                          textAlign: "center",
                          padding: "0 8px 8px",
                          color: IsTallied ? "#198754" : "#dc3545",
                        }}
                      >
                        {ListJournals.length === 0
                          ? ""
                          : IsTallied
                          ? "Debit and Credit are tallied"
                          : "Debit and Credit are not tallied"}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-12 col-sm-12 mb-2">
                    <label>Remark</label>
                    <input
                      type="text"
                      aria-label="Journal Remark"
                      onClick={(e) => e.target.select()}
                      className="form-control"
                      placeholder="Enter Remark"
                      value={strSumRemarks}
                      onChange={(e) => setstrSumRemarks(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              {IsLedgerOpen && (
                <SearchLedger
                  DBName={strDBName}
                  Close={() => setIsLedgerOpen(false)}
                  SelectLedger={SelectLedger}
                />
              )}
              {IsJournalOpen && (
                <JournalView
                  DBName={strDBName}
                  BranchId={iBranchId}
                  Close={() => setIsJournalOpen(false)}
                  JournalView={View__JournalEntry}
                />
              )}
              <Messagedialogbox />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default JournalEntry;